import { Tour } from "../interfaces/itinerary.interface";
import {
  PromotionCalculationResponse,
  PromotionDeduction,
  PromotionPax,
} from "../interfaces/promotion.interface";

export interface PaxPriceItem {
  category: string;
  price: number | string;
  price_type?: string;
}

// Mirrors the backend Bookings::BulkCreate#booking_promotion_pax for the B2C2B
// customer context: dta / subtract_others / insurance_rebate / already_deducted
// are not deducted for the customer here, so they are sent as 0. The visa fee
// IS deducted per pax in this flow, so it must be forwarded.
// Infants are excluded because the backend pax payload only covers bookings,
// and source_index must stay aligned with the booking order of non-infant pax.
// The tier price for "single_supplement" already includes the supplement
// (the backend serializer adds it) — do not add tour.single_supplement_price.
export const buildPromotionPax = (items: PaxPriceItem[], tour: Tour): PromotionPax[] =>
  items
    .filter(item => item.category && item.category !== "infant")
    .map((item, index) => ({
      source_type: "booking",
      source_index: index,
      category: item.category,
      price: Number(item.price) || 0,
      price_type: item.price_type || "",
      dta: 0,
      visa_fee: Number(tour?.visa_fee || 0),
      subtract_others: 0,
      insurance_rebate: 0,
      compulsory_additional_fee: Number(tour?.compulsory_additional_fee || 0),
      addon_others: Number(tour?.addon_others || 0),
      already_deducted: 0,
    }));

const toAmount = (value: string | number | null | undefined): number => Number(value ?? 0) || 0;

export const parsePromotionCalculation = (
  res: PromotionCalculationResponse
): { deductions: PromotionDeduction[]; total: number } => {
  if (!res?.success) return { deductions: [], total: 0 };

  return {
    // A successful calculation can still contain promotions that matched no pax
    // (zero entitled pax / amount) — those must not render as deduction rows.
    deductions: (res.promotions || [])
      .map(promotion => ({
        id: promotion.id,
        title: promotion.title,
        entitledPaxCount: promotion.entitled_pax_count,
        amount: toAmount(promotion.total_deduction_amount),
      }))
      .filter(deduction => deduction.entitledPaxCount > 0 && deduction.amount > 0),
    // The server total is authoritative — never sum the rows locally.
    total: toAmount(res.total_deduction_amount),
  };
};
